if (!customElements.get('almila-google-reviews')) {
  // Scroll-snap carousel for the server-rendered review cards (same behaviour as the home
  // categories carousel) plus a "read more" toggle shown only on reviews that are clamped.
  class AlmilaGoogleReviews extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.viewport = this.querySelector('[data-review-viewport]');
      this.cards = [...this.querySelectorAll('[data-review-card]')];
      this.previous = this.querySelector('[data-review-prev]');
      this.next = this.querySelector('[data-review-next]');
      if (!this.viewport) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.previous?.addEventListener('click', () => this.show(this.active - 1), options);
      this.next?.addEventListener('click', () => this.show(this.active + 1), options);
      this.querySelectorAll('[data-review-more]').forEach(button => {
        button.addEventListener('click', () => this.toggle(button), options);
      });
      this.viewport.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
      }, { ...options, passive: true });
      this.viewport.addEventListener('focusin', event => {
        const index = this.cards.indexOf(event.target.closest('[data-review-card]'));
        if (index < 0) return;
        const card = this.cards[index].getBoundingClientRect();
        const view = this.viewport.getBoundingClientRect();
        if (card.left < view.left + 4 || card.right > view.right - 4) this.show(index, 'auto');
      }, options);
      this.observer = new ResizeObserver(() => { this.measure(); this.update(); });
      this.observer.observe(this.viewport);
      this.setAttribute('data-carousel-ready', '');
      this.measure();
      this.update();
    }

    // Show "read more" only where the clamped text actually overflows; expanded texts keep theirs.
    measure() {
      this.querySelectorAll('[data-review-text]').forEach(text => {
        const button = text.nextElementSibling;
        if (!button?.matches('[data-review-more]') || button.getAttribute('aria-expanded') === 'true') return;
        button.hidden = text.scrollHeight <= text.clientHeight + 1;
      });
    }

    toggle(button) {
      const card = button.closest('[data-review-card]');
      const expanded = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(expanded));
      card.toggleAttribute('data-expanded', expanded);
      button.querySelector('[data-more-label]').hidden = expanded;
      button.querySelector('[data-less-label]').hidden = !expanded;
    }

    position(index) {
      const view = this.viewport.getBoundingClientRect();
      const inset = parseFloat(getComputedStyle(this.viewport).paddingLeft) || 0;
      return this.viewport.scrollLeft + this.cards[index].getBoundingClientRect().left - view.left - inset;
    }

    show(index, behavior) {
      if (!this.controller || !this.cards.length) return;
      const target = Math.max(0, Math.min(index, this.cards.length - 1));
      this.viewport.scrollTo({ left: this.position(target), behavior: behavior || (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth') });
    }

    update() {
      if (!this.controller || !this.cards.length) return;
      const maximum = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const left = this.viewport.scrollLeft;
      this.active = this.cards.reduce((nearest, _, index) => Math.abs(this.position(index) - left) < Math.abs(this.position(nearest) - left) ? index : nearest, 0);
      const atStart = left <= 1;
      const atEnd = left >= maximum - 1;
      if (this.previous && this.next) {
        // A focused arrow that becomes disabled would drop keyboard focus to the page: hand it to the other arrow.
        if (atStart && !atEnd && document.activeElement === this.previous) this.next.focus({ preventScroll: true });
        if (atEnd && !atStart && document.activeElement === this.next) this.previous.focus({ preventScroll: true });
      }
      if (this.previous) this.previous.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;
      this.toggleAttribute('data-scrollable', maximum > 1);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.removeAttribute('data-carousel-ready');
    }
  }
  customElements.define('almila-google-reviews', AlmilaGoogleReviews);
  const components = scope => [...(scope.matches?.('almila-google-reviews') ? [scope] : []), ...scope.querySelectorAll('almila-google-reviews')];
  document.addEventListener('shopify:section:load', event => components(event.target).forEach(element => { element.disconnectedCallback(); element.connectedCallback(); }));
  document.addEventListener('shopify:section:unload', event => components(event.target).forEach(element => element.disconnectedCallback()));
  document.addEventListener('shopify:block:select', event => {
    const element = event.target.closest('almila-google-reviews');
    if (element) element.show(element.cards.indexOf(event.target.closest('[data-review-card]')), 'auto');
  });
}
